import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Panel, Loading } from "@/components/kit";

const COLORS = { paid: "#2563eb", sick: "#d97706", unpaid: "#64748b", exceptional: "#7c3aed" };
const shift = (m, k) => { const [y, mo] = m.split("-").map(Number); const d = new Date(Date.UTC(y, mo - 1 + k, 1)); return d.toISOString().slice(0, 7); };
const cellStyle = (lv) => lv.status === "pending"
  ? { backgroundImage: `repeating-linear-gradient(45deg, ${COLORS[lv.type]}55 0 3px, transparent 3px 6px)`, border: `1px dashed ${COLORS[lv.type]}` }
  : { background: COLORS[lv.type] };

const Legend = ({ t }) => (
  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="leave-calendar-legend">
    {Object.keys(COLORS).map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: COLORS[k] }} />{t(`leave_t_${k}`)}</span>)}
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={cellStyle({ type: "paid", status: "pending" })} />{t("leave_st_pending")}</span>
    <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-[#B8000F]" />{t("leave_cal_overlap")}</span>
  </div>
);

const Grid = ({ d, t }) => {
  const byEmp = useMemo(() => {
    const m = {};
    d.leaves.forEach((lv) => { (m[lv.employee_id] ||= []).push(lv); });
    return m;
  }, [d]);
  const at = (eid, date) => (byEmp[eid] || []).find((lv) => lv.start <= date && date <= lv.end);
  return (
    <div className="overflow-x-auto" data-testid="leave-calendar-grid">
      <table className="w-full border-separate border-spacing-0 text-[11px]">
        <thead><tr>
          <th className="sticky start-0 z-10 min-w-[150px] bg-white px-2 py-1 text-start font-semibold text-slate-500">{t("leave_employee")}</th>
          {d.days.map((x) => (
            <th key={x.date} className={`w-7 min-w-[28px] px-0 py-1 text-center font-medium ${x.weekday === 6 ? "bg-slate-100 text-slate-400" : "text-slate-500"}`}>
              <div className="num">{+x.date.slice(8)}</div>
              {d.overlaps[x.date] && <span className="block" title={`${d.overlaps[x.date]} ${t("leave_cal_absent")}`} data-testid={`leave-cal-overlap-${x.date}`}><AlertTriangle className="mx-auto h-3 w-3 text-[#B8000F]" /></span>}
            </th>))}
        </tr></thead>
        <tbody>{d.employees.map((e) => (
          <tr key={e.id} data-testid={`leave-cal-row-${e.code}`}>
            <td className="sticky start-0 z-10 whitespace-nowrap border-t bg-white px-2 py-1 font-medium text-slate-700">{e.first_name} {e.last_name}<span className="ms-1 text-slate-400">{e.code}</span></td>
            {d.days.map((x) => {
              const lv = x.weekday !== 6 && at(e.id, x.date);
              return <td key={x.date} className={`h-7 border-t p-0.5 ${x.weekday === 6 ? "bg-slate-100" : ""}`}>
                {lv && <div className="h-full w-full rounded-sm" style={cellStyle(lv)} title={`${t(`leave_t_${lv.type}`)} · ${t(`leave_st_${lv.status}`)}`} data-testid={`leave-cal-cell-${e.code}-${x.date}`} />}
              </td>;
            })}
          </tr>))}</tbody>
      </table>
    </div>
  );
};

export const LeaveCalendar = ({ reload }) => {
  const { api, t, lang } = useApp();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); }, [month]);
  useEffect(() => { api.get("/payroll/leaves/calendar", { params: { month } }).then((r) => setD(r.data)); }, [api, month, reload]);
  const label = new Date(`${month}-01T00:00:00`).toLocaleDateString(lang === "ar" ? "ar-TN" : "fr-FR", { month: "long", year: "numeric" });
  const nav = (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" onClick={() => setMonth(shift(month, -1))} data-testid="leave-cal-prev"><ChevronLeft className="h-4 w-4 rtl:rotate-180" /></Button>
      <span className="min-w-[120px] text-center text-sm font-semibold capitalize" data-testid="leave-cal-month">{label}</span>
      <Button size="sm" variant="ghost" onClick={() => setMonth(shift(month, 1))} data-testid="leave-cal-next"><ChevronRight className="h-4 w-4 rtl:rotate-180" /></Button>
    </div>
  );
  return (
    <Panel title={t("leave_calendar")} action={nav} testid="leave-calendar">
      {!d ? <Loading /> : <><Grid d={d} t={t} /><Legend t={t} /></>}
    </Panel>
  );
};
